import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import {
  fetchEbooks,
  isEbookUnlocked,
  ebookUnlockExpiresIn,
  unlockEbook,
  Ebook,
} from '../api/ebookApi';
import {
  fetchPaidBooks,
  fetchMyPurchases,
  PaidBook,
} from '../api/paidBookApi';
import {
  initialiseBilling,
  getProducts,
  onProductsUpdated,
  purchase as purchasePaidBook,
} from '../utils/playBillingService';
import { showRewardedAd, isNativeMobile } from '../utils/adMobService';
import { isAndroid } from '../utils/platformUtils';
import { getPremiumSubscriber } from '../stores/adFreeSubscriberStore';
import { isGuestUser } from '../utils/guestUtils';
import './EbookScreen.css';

type Lang = 'bn' | 'it' | 'en';

// ─── Google Play billing ─────────────────────────────────────────────────────
//
// All billing goes through playBillingService. This screen deliberately holds
// NO direct handle on window.CdvPurchase: ordering an offer straight from here
// skips the plugin's approved -> verified chain, which is the only thing that
// calls our verify-play-purchase Edge Function and then finish(). A purchase
// that is never finished is auto-refunded by Play after three days, so the user
// is charged, loses the money back, and never receives the book.


const CONTENT = {
  bn: {
    title: 'ই-বুক',
    subtitle: 'পড়ুন এবং শিখুন',
    search: 'বই খুঁজুন...',
    unlock: 'বিজ্ঞাপন দেখে আনলক করুন 🎬',
    open: 'পড়তে শুরু করুন 📖',
    locked: 'লক করা',
    unlocked: 'আনলক করা',
    expiresIn: 'মেয়াদ শেষ',
    days: 'দিন',
    hours: 'ঘণ্টা',
    all: 'সব',
    beginner: 'শিক্ষার্থী',
    intermediate: 'মধ্যবর্তী',
    advanced: 'উন্নত',
    watchingAd: 'বিজ্ঞাপন চলছে...',
    adNote: 'একটি ছোট ভিডিও দেখুন (১৫–৩০ সেকেন্ড) এবং ৭ দিনের জন্য বইটি আনলক করুন।',
    adTitle: 'বইটি আনলক করুন',
    adCancel: 'বাতিল করুন',
    adSuccess: 'আনলক হয়েছে! 🎉',
    noPdf: 'পিডিএফ এখনো উপলব্ধ নয়',
    premiumTitle: 'প্রিমিয়াম বই',
    premiumNote: 'একবার কিনলে আজীবন আপনার। কোনো বিজ্ঞাপন নেই, মেয়াদ শেষ হয় না।',
    buy: 'কিনুন',
    owned: '✓ কেনা হয়েছে',
    buying: 'অপেক্ষা করুন...',
    signInToBuy: 'কিনতে সাইন ইন করুন',
    billingUnavailable: 'এখন কেনা যাচ্ছে না। একটু পরে আবার চেষ্টা করুন।',
    purchaseFailed: 'কেনা সম্পন্ন হয়নি। আবার চেষ্টা করুন।',
    purchasePending: 'পেমেন্ট যাচাই করা হচ্ছে...',
  },
  it: {
    title: 'E-Book',
    subtitle: 'Leggi e Impara',
    search: 'Cerca libro...',
    unlock: 'Guarda un annuncio per sbloccare 🎬',
    open: 'Inizia a Leggere 📖',
    locked: 'Bloccato',
    unlocked: 'Sbloccato',
    expiresIn: 'Scade tra',
    days: 'giorni',
    hours: 'ore',
    all: 'Tutti',
    beginner: 'Principiante',
    intermediate: 'Intermedio',
    advanced: 'Avanzato',
    watchingAd: 'Annuncio in corso...',
    adNote: 'Guarda un breve video (15–30 sec) per sbloccare il libro per 7 giorni.',
    adTitle: 'Sblocca il Libro',
    adCancel: 'Annulla',
    adSuccess: 'Sbloccato! 🎉',
    noPdf: 'PDF non ancora disponibile',
    premiumTitle: 'Libri Premium',
    premiumNote: 'Acquisto unico, tuo per sempre. Nessun annuncio, nessuna scadenza.',
    buy: 'Acquista',
    owned: '✓ Acquistato',
    buying: 'Attendere...',
    signInToBuy: 'Accedi per acquistare',
    billingUnavailable: 'Acquisto non disponibile ora. Riprova tra poco.',
    purchaseFailed: 'Acquisto non completato. Riprova.',
    purchasePending: 'Verifica del pagamento...',
  },
  en: {
    title: 'E-Book',
    subtitle: 'Read & Learn',
    search: 'Search books...',
    unlock: 'Watch Ad to Unlock 🎬',
    open: 'Start Reading 📖',
    locked: 'Locked',
    unlocked: 'Unlocked',
    expiresIn: 'Expires in',
    days: 'days',
    hours: 'hrs',
    all: 'All',
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
    watchingAd: 'Watching ad...',
    adNote: 'Watch a short video (15–30 sec) to unlock the book for 7 days.',
    adTitle: 'Unlock the Book',
    adCancel: 'Cancel',
    adSuccess: 'Unlocked! 🎉',
    noPdf: 'PDF not available yet',
    premiumTitle: 'Premium Books',
    premiumNote: 'One-time purchase, yours for life. No ads, never expires.',
    buy: 'Buy',
    owned: '✓ Owned',
    buying: 'Please wait...',
    signInToBuy: 'Sign in to buy',
    billingUnavailable: 'Purchases are unavailable right now. Try again shortly.',
    purchaseFailed: 'Purchase did not complete. Please try again.',
    purchasePending: 'Verifying your payment...',
  },
};

const WEB_AD_SECONDS = 5;

const EbookScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const lang = currentLanguage as Lang;
  const t = CONTENT[lang] || CONTENT.en;


  const [ebooks, setEbooks] = useState<Ebook[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string>('all');

  // Ad unlock modal state
  const [adModal, setAdModal] = useState<Ebook | null>(null);
  const [adPhase, setAdPhase] = useState<'prompt' | 'loading' | 'watching' | 'success'>('prompt');
  const [adCountdown, setAdCountdown] = useState(WEB_AD_SECONDS);
  const ebookModalInsRef = useRef<HTMLModElement>(null);
  const ebookAdPushedRef = useRef(false);

  // Re-render tick to update expiry display
  const [, setTick] = useState(0);

  const [userId, setUserId] = useState<string | undefined>(undefined);

  // ─── Paid books (Google Play) ─────────────────────────────────────────────
  // Android native only. `isAndroid()` is already false unless
  // Capacitor.isNativePlatform(), so this one flag covers web, the CRA dev
  // server, and — critically — keeps Play billing out of any iOS build, where
  // offering it would be a store-policy violation. Computed once: the platform
  // cannot change while the screen is mounted.
  const [showPaidBooks] = useState(() => isAndroid());
  const [paidBooks, setPaidBooks] = useState<PaidBook[]>([]);
  const [ownedProductIds, setOwnedProductIds] = useState<Set<string>>(new Set());
  const [playPrices, setPlayPrices] = useState<Record<string, string>>({});
  const [buyingProductId, setBuyingProductId] = useState<string | null>(null);
  const [paidError, setPaidError] = useState<string>('');

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data?.user?.id ?? undefined);
    });
  }, []);

  // Catalogue. Fetched only on Android so no other platform pays for the query.
  useEffect(() => {
    if (!showPaidBooks) return;
    fetchPaidBooks().then(setPaidBooks);
  }, [showPaidBooks]);

  // Entitlements. `userId` is undefined on first render and for guests;
  // fetchMyPurchases handles both by returning [].
  const refreshPurchases = useCallback(async () => {
    if (!showPaidBooks) return new Set<string>();
    const rows = await fetchMyPurchases(userId);
    const owned = new Set(rows.map(r => r.play_product_id));
    setOwnedProductIds(owned);
    return owned;
  }, [showPaidBooks, userId]);

  useEffect(() => {
    refreshPurchases();
  }, [refreshPurchases]);

  // Bring billing up and mirror Play's localised prices into state.
  //
  // initialiseBilling() is what registers the products and installs the
  // approved -> verified -> finish chain; without it Play knows nothing about
  // our product IDs and every Buy tap dead-ends. onProductsUpdated fires when
  // Play answers, which is some time after `deviceready`, so prices are pulled
  // on the callback rather than polled. A book with no Play price yet falls
  // back to price_display from the catalogue, so a slow response degrades to a
  // static label rather than a blank card.
  useEffect(() => {
    if (!showPaidBooks) return;

    let cancelled = false;

    const syncPrices = () => {
      if (cancelled) return;
      const next: Record<string, string> = {};
      getProducts().forEach(product => {
        if (product.price) next[product.productId] = product.price;
      });
      setPlayPrices(prev => {
        const same =
          Object.keys(next).length === Object.keys(prev).length &&
          Object.keys(next).every(key => prev[key] === next[key]);
        return same ? prev : next;
      });
    };

    const unsubscribe = onProductsUpdated(syncPrices);
    void initialiseBilling().then(syncPrices);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [showPaidBooks]);

  const handleBuyClick = useCallback(async (book: PaidBook) => {
    setPaidError('');

    // Guests have no Supabase session and therefore no JWT, so the Edge Function
    // that records the entitlement is unreachable for them. Sending them into
    // the Play sheet would take their money and then fail verification — route
    // to login instead of starting billing.
    if (!userId || userId === 'guest' || isGuestUser()) {
      navigate('/login');
      return;
    }

    setBuyingProductId(book.play_product_id);
    try {
      // purchase() runs the Play sheet AND the approved -> verify -> finish
      // chain. It resolves only once our Edge Function has confirmed and the
      // transaction is finished, so an ok result means the entitlement row
      // exists — not merely that Play took the money.
      const result = await purchasePaidBook(book.play_product_id);

      if (result.ok) {
        await refreshPurchases();
        return;
      }

      switch (result.code) {
        case 'cancelled':
          // Closing the sheet is a normal choice, not an error to report.
          break;
        case 'unsupported_platform':
        case 'unknown_product':
        case 'store_unavailable':
          setPaidError(t.billingUnavailable);
          break;
        case 'not_signed_in':
          navigate('/login');
          break;
        case 'timeout':
          // Verification may still land server-side; the row will appear on the
          // next visit. Say "verifying", not "failed" — the money may be gone.
          setPaidError(t.purchasePending);
          await refreshPurchases();
          break;
        default:
          setPaidError(t.purchaseFailed);
      }
    } catch {
      setPaidError(t.purchaseFailed);
    } finally {
      setBuyingProductId(null);
    }
  }, [userId, navigate, refreshPurchases, t]);

  useEffect(() => {
    setLoading(true);
    fetchEbooks().then(data => {
      setEbooks(data);
      setLoading(false);
    });
  }, []);

  // Tick every minute to refresh expiry countdown display
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Web countdown while watching ad
  useEffect(() => {
    if (adPhase !== 'watching' || isNativeMobile()) return;
    if (adCountdown <= 0) return;
    const timer = setTimeout(() => setAdCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [adPhase, adCountdown]);

  // AdSense in modal: defer push until ins has width (same slot as banner — push only for this ins)
  useEffect(() => {
    if (adPhase !== 'watching' || isNativeMobile()) {
      ebookAdPushedRef.current = false;
      return;
    }
    ebookAdPushedRef.current = false;
    const pushWhenSized = () => {
      const el = ebookModalInsRef.current;
      if (!el || ebookAdPushedRef.current) return;
      if (el.offsetWidth < 50) {
        requestAnimationFrame(pushWhenSized);
        return;
      }
      ebookAdPushedRef.current = true;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).adsbygoogle = (window as any).adsbygoogle || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).adsbygoogle.push({});
      } catch {
        ebookAdPushedRef.current = false;
      }
    };
    const id = requestAnimationFrame(() => requestAnimationFrame(pushWhenSized));
    return () => cancelAnimationFrame(id);
  }, [adPhase]);

  const handleUnlockClick = async (ebook: Ebook) => {
    if (getPremiumSubscriber() && userId) {
      await unlockEbook(ebook.id, userId);
      return;
    }
    setAdModal(ebook);
    setAdPhase('prompt');
    setAdCountdown(WEB_AD_SECONDS);
  };

  const handleWatchAd = useCallback(async () => {
    if (!adModal) return;
    if (getPremiumSubscriber() && userId) {
      await unlockEbook(adModal.id, userId);
      setAdPhase('success');
      setTimeout(() => setAdModal(null), 1400);
      return;
    }
    if (isNativeMobile()) {
      setAdPhase('loading');
      await showRewardedAd(
        async () => {
          await unlockEbook(adModal.id, userId);
          setAdPhase('success');
          setTimeout(() => setAdModal(null), 1400);
        },
        () => {
          setAdPhase('watching');
          setAdCountdown(WEB_AD_SECONDS);
        }
      );
    } else {
      setAdPhase('watching');
      setAdCountdown(WEB_AD_SECONDS);
    }
  }, [adModal, userId]);

  const handleClaimReward = useCallback(async () => {
    if (!adModal) return;
    await unlockEbook(adModal.id, userId);
    setAdPhase('success');
    setTimeout(() => setAdModal(null), 1400);
  }, [adModal, userId]);

  const getLang = (ml: { it: string; bn: string; en: string }) => ml[lang] || ml.en || ml.it || '';

  const getExpiryLabel = (ebookId: number): string => {
    const ms = ebookUnlockExpiresIn(ebookId);
    if (ms <= 0) return '';
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (days >= 1) return `${t.expiresIn} ${days} ${t.days}`;
    const hours = Math.floor(ms / (1000 * 60 * 60));
    return `${t.expiresIn} ${hours} ${t.hours}`;
  };

  const FILTERS = ['all', 'Beginner', 'Intermediate', 'Advanced'];

  const filtered = ebooks.filter(b => {
    const matchLevel = filter === 'all' || b.level === filter;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      getLang(b.title_ml).toLowerCase().includes(q) ||
      getLang(b.description_ml).toLowerCase().includes(q) ||
      (b.author || '').toLowerCase().includes(q);
    return matchLevel && matchSearch;
  });

  return (
    <div className="ebook-screen">
      <div className="screen-header">
        <button className="header-btn back-btn" onClick={() => navigate('/home')} aria-label="Go back">←</button>
        <div className="header-center">
          <h1>{t.title}</h1>
        </div>
        <button className="header-btn settings-btn" onClick={() => navigate('/settings')} aria-label="Open settings">⋯</button>
      </div>

      <LessonBannerAd />

      <div className="ebook-content">
        {/* ─── Premium Books (Google Play, Android only) ─────────────────────
            Renders nothing at all off Android native, and nothing until the
            catalogue loads — so no empty header ever flashes. Entirely separate
            from the free list below, which stays ad-unlocked. */}
        {showPaidBooks && paidBooks.length > 0 && (
          <div className="ebook-paid-section">
            <div className="ebook-subscribe-card">
              <div className="ebook-subscribe-icon">⭐</div>
              <div className="ebook-subscribe-text">
                <h3>{t.premiumTitle}</h3>
                <p>{t.premiumNote}</p>
              </div>
            </div>

            {paidError && <p className="ebook-paid-error">{paidError}</p>}

            <div className="ebook-list">
              {paidBooks.map(book => {
                const owned = ownedProductIds.has(book.play_product_id);
                const busy = buyingProductId === book.play_product_id;
                // Play's localised price when it has arrived, otherwise the
                // catalogue's static one. Play is always the truth about what
                // the user is actually charged.
                const price = playPrices[book.play_product_id] || book.price_display || '';

                return (
                  <div
                    key={book.play_product_id}
                    className={`ebook-card${owned ? ' unlocked' : ''}`}
                  >
                    {/* Cover */}
                    <div className="ebook-cover">
                      {book.cover_url ? (
                        <img src={book.cover_url} alt={getLang(book.title_ml)} />
                      ) : (
                        <div className="ebook-cover-placeholder">📖</div>
                      )}
                      <span className={`ebook-level-badge level-${(book.level || 'all').toLowerCase()}`}>
                        {book.level}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="ebook-info">
                      <h3 className="ebook-title">{getLang(book.title_ml)}</h3>
                      <p className="ebook-description">{getLang(book.description_ml)}</p>

                      {owned ? (
                        <>
                          <p className="ebook-owned-tag">{t.owned}</p>
                          <button
                            className="ebook-action-btn open"
                            onClick={() => navigate(`/paid-book/${book.play_product_id}`)}
                          >
                            {t.open}
                          </button>
                        </>
                      ) : (
                        <>
                          {price && <p className="ebook-price">{price}</p>}
                          <button
                            className="ebook-action-btn buy"
                            disabled={busy}
                            onClick={() => handleBuyClick(book)}
                          >
                            {busy ? t.buying : `${t.buy}${price ? ` · ${price}` : ''}`}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="ebook-search-bar">
          <span className="ebook-search-icon">🔍</span>
          <input
            type="text"
            placeholder={t.search}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Level filter pills */}
        <div className="ebook-filters">
          {FILTERS.map(f => (
            <button
              key={f}
              className={`ebook-filter-pill${filter === f ? ' active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? t.all : f === 'Beginner' ? t.beginner : f === 'Intermediate' ? t.intermediate : t.advanced}
            </button>
          ))}
        </div>

        {/* Book list */}
        {loading ? (
          <div className="ebook-loading">
            {[1, 2, 3].map(i => (
              <div key={i} className="ebook-skeleton" />
            ))}
          </div>
        ) : (
          <div className="ebook-list">
            {filtered.map(book => {
              const unlocked = isEbookUnlocked(book.id);
              const expiry = unlocked ? getExpiryLabel(book.id) : '';
              return (
                <div key={book.id} className={`ebook-card${unlocked ? ' unlocked' : ''}`}>
                  {/* Cover */}
                  <div className="ebook-cover">
                    {book.cover_url ? (
                      <img src={book.cover_url} alt={getLang(book.title_ml)} />
                    ) : (
                      <div className="ebook-cover-placeholder">📚</div>
                    )}
                    <span className={`ebook-level-badge level-${(book.level || 'all').toLowerCase()}`}>
                      {book.level}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="ebook-info">
                    <h3 className="ebook-title">{getLang(book.title_ml)}</h3>
                    {book.author && <p className="ebook-author">✍️ {book.author}</p>}
                    <p className="ebook-description">{getLang(book.description_ml)}</p>

                    {unlocked && expiry && (
                      <p className="ebook-expiry">⏳ {expiry}</p>
                    )}

                    {unlocked ? (
                      book.pdf_url ? (
                        <button
                          className="ebook-action-btn open"
                          onClick={() => navigate(`/ebooks/${book.id}`)}
                        >
                          {t.open}
                        </button>
                      ) : (
                        <p className="ebook-no-pdf">{t.noPdf}</p>
                      )
                    ) : (
                      <button
                        className="ebook-action-btn unlock"
                        onClick={() => handleUnlockClick(book)}
                      >
                        {t.unlock}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ad Unlock Modal */}
      {adModal && (
        <div className="ebook-ad-backdrop" onClick={e => {
          if (e.target === e.currentTarget && adPhase !== 'watching' && adPhase !== 'loading') setAdModal(null);
        }}>
          <div className="ebook-ad-modal">
            {adPhase === 'prompt' && (
              <>
                <div className="ebook-ad-icon">🔓</div>
                <h2 className="ebook-ad-title">{t.adTitle}</h2>
                <p className="ebook-ad-book-name">"{getLang(adModal.title_ml)}"</p>
                <p className="ebook-ad-note">{t.adNote}</p>
                <button className="ebook-ad-watch-btn" onClick={handleWatchAd}>{t.unlock}</button>
                <button className="ebook-ad-cancel-btn" onClick={() => setAdModal(null)}>{t.adCancel}</button>
              </>
            )}
            {adPhase === 'loading' && (
              <>
                <div className="ebook-ad-icon">📺</div>
                <p className="ebook-ad-watching">{t.watchingAd}</p>
                <div className="ebook-ad-spinner" />
              </>
            )}
            {adPhase === 'watching' && (
              <>
                <div className="ebook-ad-area">
                  <ins
                    ref={ebookModalInsRef}
                    className="adsbygoogle"
                    style={{ display: 'block', width: '100%', minWidth: 280, minHeight: 120 }}
                    data-ad-client="ca-pub-7754802589108768"
                    data-ad-slot="3522444311"
                    data-ad-format="auto"
                    data-full-width-responsive="true"
                  />
                </div>
                <p className="ebook-ad-watching">{t.watchingAd}</p>
                <div className="ebook-ad-countdown-wrap">
                  {adCountdown > 0 ? (
                    <div className="ebook-ad-countdown">{adCountdown}</div>
                  ) : (
                    <button className="ebook-ad-claim-btn" onClick={handleClaimReward}>
                      ✓ {t.unlock}
                    </button>
                  )}
                </div>
              </>
            )}
            {adPhase === 'success' && (
              <>
                <div className="ebook-ad-success-icon">🎉</div>
                <h2 className="ebook-ad-title">{t.adSuccess}</h2>
                <p className="ebook-ad-book-name">"{getLang(adModal.title_ml)}"</p>
                <div className="ebook-ad-success-loader" />
              </>
            )}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default EbookScreen;
